import { Button } from "../components/Button";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
const ButtonsSwitchContext = React.createContext(null);
const useButtonsSwitch = () => {
    const ctx = React.useContext(ButtonsSwitchContext);
    if (!ctx) {
        throw new Error("ButtonsSwitch must be used within a ButtonsSwitchList component");
    }
    return ctx;
};
const listStyles = cva(cn("inline-flex items-center gap-1", "box-border bg-background border border-border-dark"), {
    variants: {
        fullWidth: {
            true: "w-full",
            false: "",
        },
        size: {
            xs: "rounded-xl p-[3px]",
            sm: "rounded-2xl p-1",
            md: "rounded-3xl p-1.5",
        },
    },
    defaultVariants: {
        fullWidth: false,
        size: "sm",
    },
});
/**
 * The container of a segmented, single-select toggle: it owns the selected value
 * (controlled via `value`/`onValueChange` or uncontrolled via `defaultValue`) for
 * its ButtonsSwitch children. Use it to switch between a small set of mutually
 * exclusive views or modes; for triggering actions, use Button or ButtonGroup.
 * @summary Segmented single-select toggle container.
 */
export const ButtonsSwitchList = React.forwardRef(({ className, children, size = "sm", value, defaultValue, onValueChange, disabled, fullWidth, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const isControlled = value !== undefined;
    const selected = isControlled ? value : internalValue;
    const handleChange = React.useCallback((next) => {
        if (!isControlled) {
            setInternalValue(next);
        }
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(next);
    }, [isControlled, onValueChange]);
    const context = React.useMemo(() => ({ value: selected, onValueChange: handleChange, size, disabled }), [selected, handleChange, size, disabled]);
    return (React.createElement("div", { ref: ref, role: "tablist", "aria-orientation": "horizontal", className: cn(listStyles({ fullWidth, size }), className), ...props },
        React.createElement(ButtonsSwitchContext.Provider, { value: context }, children)));
});
ButtonsSwitchList.displayName = "ButtonsSwitchList";
/**
 * One option of a segmented toggle, identified by its `value` and rendered with a
 * `label`. Must be rendered inside a ButtonsSwitchList, which manages selection.
 * @summary Single option of a ButtonsSwitchList.
 */
export const ButtonsSwitch = React.forwardRef(({ className, value, label, icon, disabled, onClick, ...props }, ref) => {
    const { value: selected, onValueChange, size, disabled: groupDisabled, } = useButtonsSwitch();
    const isActive = selected === value;
    const isDisabled = disabled || groupDisabled;
    const handleClick = (e) => {
        if (isDisabled) {
            return;
        }
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(value);
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    return (React.createElement(Button, { ref: ref, role: "tab", "aria-selected": isActive, size: size, variant: isActive ? "outline" : "ghost", label: label, icon: icon, className: cn(!isActive && "border border-transparent", className), disabled: isDisabled, onClick: handleClick, ...props }));
});
ButtonsSwitch.displayName = "ButtonsSwitch";
//# sourceMappingURL=ButtonsSwitch.js.map